import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Car, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { isRideDriverEmail } from '@/config/features';
import RideDriverMap from '@/components/ride-driver/RideDriverMap';
import RideDriverProfile from '@/components/ride-driver/RideDriverProfile';
import RideDriverMenu from '@/components/ride-driver/RideDriverMenu';
import RideRequestPopup from '@/components/ride-driver/RideRequestPopup';
import ActiveRideNavigation from '@/components/ride-driver/ActiveRideNavigation';
import { useToast } from '@/hooks/use-toast';

const normalizeRideType = (value: string | null | undefined): 'economy' | 'comfort' | 'comfort_plus' | 'comfort_xl' | null => {
  if (!value) return null;
  if (value === 'economy' || value === 'comfort' || value === 'comfort_plus' || value === 'comfort_xl') return value;
  return null;
};

const RideDriverDashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [isOnline, setIsOnline] = useState(false);
  const [activeTab, setActiveTab] = useState<'drive' | 'profile'>('drive');
  const [todayEarnings, setTodayEarnings] = useState(0);
  const [todayRides, setTodayRides] = useState(0);
  const [incomingRequest, setIncomingRequest] = useState<any>(null);
  const [activeRide, setActiveRide] = useState<any>(null);
  const [driverLocation, setDriverLocation] = useState<{ lat: number; lng: number } | null>(null);
  const latestDriverLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  const isRideDriver = isRideDriverEmail(user?.email);

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && user && !isRideDriver) navigate('/home');
  }, [user, authLoading, isRideDriver, navigate]);

  // Ensure ride driver profile exists and carries approved ride vehicle metadata
  useEffect(() => {
    if (!user) return;

    const ensureRideProfile = async () => {
      const [{ data: existingProfile }, { data: approvedApplication, error: appError }] = await Promise.all([
        supabase
          .from('ride_driver_profiles')
          .select('ride_types_allowed, vehicle_make, vehicle_model, vehicle_color, vehicle_plate, license_number, is_online')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('driver_applications')
          .select('vehicle_type, vehicle_make, vehicle_color, vehicle_plate, license_number')
          .eq('user_id', user.id)
          .eq('driver_type', 'ride')
          .eq('status', 'approved')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (appError) {
        console.error('Failed to read approved ride application:', appError);
      }

      const approvedRideType = normalizeRideType(approvedApplication?.vehicle_type ?? null);
      const existingRideTypes = existingProfile?.ride_types_allowed?.filter((v: string | null) => !!normalizeRideType(v)) as string[] | undefined;
      const effectiveRideTypes =
        existingRideTypes && existingRideTypes.length > 0
          ? existingRideTypes
          : approvedRideType
            ? [approvedRideType]
            : ['comfort_plus'];

      const { error } = await supabase
        .from('ride_driver_profiles')
        .upsert(
          {
            user_id: user.id,
            ride_types_allowed: effectiveRideTypes as any,
            vehicle_make: existingProfile?.vehicle_make ?? approvedApplication?.vehicle_make ?? null,
            vehicle_color: existingProfile?.vehicle_color ?? approvedApplication?.vehicle_color ?? null,
            vehicle_plate: existingProfile?.vehicle_plate ?? approvedApplication?.vehicle_plate ?? null,
            license_number: existingProfile?.license_number ?? approvedApplication?.license_number ?? null,
            is_online: existingProfile?.is_online ?? false,
          },
          { onConflict: 'user_id' },
        );

      if (error) {
        console.error('Failed to ensure ride driver profile:', error);
      }
    };

    ensureRideProfile();
  }, [user]);

  // Check for any existing active ride on mount
  useEffect(() => {
    if (!user) return;
    const checkActiveRide = async () => {
      const { data } = await supabase
        .from('ride_requests')
        .select('*')
        .eq('driver_id', user.id)
        .in('status', ['accepted', 'driver_en_route', 'in_progress'] as any)
        .order('accepted_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setActiveRide(data);
        setIsOnline(true);
      }
    };
    checkActiveRide();
  }, [user]);

  // Get driver location and continuously update when online
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setDriverLocation(loc);
        latestDriverLocationRef.current = loc;
      },
      () => {}
    );

    if (!isOnline || !user) return;

    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setDriverLocation(loc);
        latestDriverLocationRef.current = loc;

        await supabase
          .from('ride_driver_profiles')
          .upsert({
            user_id: user.id,
            is_online: true,
            current_latitude: loc.lat,
            current_longitude: loc.lng,
            last_location_update: new Date().toISOString(),
          }, { onConflict: 'user_id' });
      },
      () => {},
      { enableHighAccuracy: true }
    );

    const locationUpdateInterval = setInterval(async () => {
      const latestLoc = latestDriverLocationRef.current;
      if (!latestLoc) return;

      await supabase
        .from('ride_driver_profiles')
        .upsert({
          user_id: user.id,
          is_online: true,
          current_latitude: latestLoc.lat,
          current_longitude: latestLoc.lng,
          last_location_update: new Date().toISOString(),
        }, { onConflict: 'user_id' });
    }, 10000);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(locationUpdateInterval);
    };
  }, [isOnline, user]);

  // Fetch today's earnings
  useEffect(() => {
    if (!user) return;
    const fetchTodayStats = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { data } = await supabase
        .from('ride_requests')
        .select('final_fare, estimated_fare')
        .eq('driver_id', user.id)
        .eq('status', 'completed')
        .gte('completed_at', today.toISOString());
      if (data) {
        setTodayEarnings(data.reduce((sum, r) => sum + (r.final_fare || r.estimated_fare || 0), 0));
        setTodayRides(data.length);
      }
    };
    fetchTodayStats();
  }, [user]);

  // Listen and poll for incoming ride requests (only when no active ride)
  useEffect(() => {
    if (!user || !isOnline || activeRide) return;

    let isMounted = true;
    let allowedRideTypes: string[] = [];

    const maybeShowRequest = (request: any) => {
      if (!isMounted || !request) return;
      if (request.driver_id) return;
      if (request.customer_id === user.id) return;
      if (allowedRideTypes.length > 0 && !allowedRideTypes.includes(request.ride_type)) return;
      setIncomingRequest((prev: any) => (prev?.id === request.id ? prev : request));
    };

    const loadPendingRequest = async () => {
      const { data: profile } = await supabase
        .from('ride_driver_profiles')
        .select('ride_types_allowed')
        .eq('user_id', user.id)
        .maybeSingle();

      allowedRideTypes = (profile?.ride_types_allowed || []) as string[];
      if (allowedRideTypes.length === 0) {
        allowedRideTypes = ['comfort_plus'];
      }

      const { data: pending } = await supabase
        .from('ride_requests')
        .select('*')
        .eq('status', 'pending')
        .is('driver_id', null)
        .in('ride_type', allowedRideTypes as any)
        .neq('customer_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (pending) maybeShowRequest(pending);
    };

    loadPendingRequest();
    const pollId = setInterval(loadPendingRequest, 8000);

    const channel = supabase
      .channel(`ride-requests-for-driver-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ride_requests',
        filter: 'status=eq.pending'
      }, (payload) => {
        maybeShowRequest(payload.new);
      })
      .subscribe();

    return () => {
      isMounted = false;
      clearInterval(pollId);
      supabase.removeChannel(channel);
    };
  }, [user, isOnline, activeRide]);

  const toggleOnlineStatus = async () => {
    const newStatus = !isOnline;
    setIsOnline(newStatus);

    if (!user) return;

    const latestLoc = latestDriverLocationRef.current;
    await supabase
      .from('ride_driver_profiles')
      .upsert({
        user_id: user.id,
        is_online: newStatus,
        current_latitude: latestLoc?.lat ?? null,
        current_longitude: latestLoc?.lng ?? null,
        last_location_update: new Date().toISOString(),
      }, { onConflict: 'user_id' });
  };

  const handleAcceptRide = useCallback(async (requestId: string) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('ride_requests')
      .update({
        driver_id: user.id,
        status: 'accepted' as any,
        accepted_at: new Date().toISOString(),
      })
      .eq('id', requestId)
      .is('driver_id', null)
      .eq('status', 'pending')
      .select()
      .maybeSingle();

    if (error || !data) {
      toast({ title: 'Unable to accept ride', description: 'Another driver may have accepted it.', variant: 'destructive' });
      setIncomingRequest(null);
      return;
    }

    setIncomingRequest(null);
    setActiveRide(data);
    toast({ title: 'Ride accepted!', description: 'Navigate to pickup location.' });
  }, [user, toast]);

  const handleDeclineRide = useCallback(async () => {
    setIncomingRequest(null);
  }, []);

  const handleRideComplete = useCallback(() => {
    setActiveRide(null);
    toast({ title: 'Ride completed!', description: 'Great job!' });
    // Refresh earnings
    if (user) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      supabase
        .from('ride_requests')
        .select('final_fare, estimated_fare')
        .eq('driver_id', user.id)
        .eq('status', 'completed')
        .gte('completed_at', today.toISOString())
        .then(({ data }) => {
          if (data) {
            setTodayEarnings(data.reduce((sum, r) => sum + (r.final_fare || r.estimated_fare || 0), 0));
            setTodayRides(data.length);
          }
        });
    }
  }, [user, toast]);

  const handleRideCancel = useCallback(async () => {
    if (!activeRide || !user) return;
    await supabase
      .from('ride_requests')
      .update({
        status: 'cancelled' as any,
        cancellation_reason: 'Cancelled by driver',
        cancelled_by: user.id,
      })
      .eq('id', activeRide.id);
    setActiveRide(null);
    toast({ title: 'Ride cancelled' });
  }, [activeRide, user, toast]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!user) return null;

  // If there's an active ride, show the navigation view
  if (activeRide) {
    return (
      <div className="h-screen flex flex-col bg-background">
        <div className="flex-1">
          <ActiveRideNavigation
            ride={activeRide}
            driverLocation={driverLocation}
            onComplete={handleRideComplete}
            onCancel={handleRideCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {activeTab === 'drive' ? (
        <div className="flex-1 relative">
          <RideDriverMap />
          
          <div className="absolute top-0 left-0 right-0 z-[1000] flex items-center justify-between px-4 py-3">
            <div className="bg-foreground text-background px-4 py-2 rounded-full font-bold text-lg shadow-lg">
              D{todayEarnings.toFixed(0)}
            </div>
            <Button
              onClick={toggleOnlineStatus}
              size="sm"
              className={`rounded-full px-5 shadow-lg font-semibold ${
                isOnline
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <Car className="w-4 h-4 mr-2" />
              {isOnline ? 'Go Offline' : 'Go Online'}
            </Button>
          </div>

          {isOnline && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-background/90 backdrop-blur-sm rounded-full px-5 py-2.5 shadow-lg flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium">
                {todayRides > 0 ? `${todayRides} rides today` : 'Waiting for rides...'}
              </span>
            </div>
          )}

          {!isOnline && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-background/90 backdrop-blur-sm rounded-full px-5 py-2.5 shadow-lg">
              <span className="text-sm text-muted-foreground font-medium">You're offline</span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="absolute top-4 right-4 z-30">
            <RideDriverMenu />
          </div>
          <RideDriverProfile />
        </div>
      )}

      <div className="bg-background border-t border-border flex">
        <button
          onClick={() => setActiveTab('drive')}
          className={`flex-1 flex flex-col items-center py-3 gap-1 transition-colors ${
            activeTab === 'drive' ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          <Car className="w-6 h-6" />
          <span className="text-xs font-medium">Drive</span>
        </button>
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex-1 flex flex-col items-center py-3 gap-1 transition-colors ${
            activeTab === 'profile' ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          <User className="w-6 h-6" />
          <span className="text-xs font-medium">Profile</span>
        </button>
      </div>

      {incomingRequest && (
        <RideRequestPopup
          request={incomingRequest}
          driverLocation={driverLocation}
          onAccept={handleAcceptRide}
          onDecline={handleDeclineRide}
        />
      )}
    </div>
  );
};

export default RideDriverDashboardPage;
